import { useEffect, useState } from "react";
import { useAppUpdate, APP_VERSION } from "@/hooks/useAppUpdate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Download,
  X,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Smartphone,
} from "lucide-react";

export const UpdateAlert = () => {
  const {
    hasUpdate,
    isForceUpdate,
    versionInfo,
    isChecking,
    checkForUpdates,
    downloadUpdate,
    dismissUpdate,
    isNativeApp,
    currentVersion,
  } = useAppUpdate();

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (hasUpdate && !isForceUpdate) {
      // Mostrar banner con un pequeño delay para no interrumpir el inicio
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [hasUpdate, isForceUpdate]);

  // No mostrar nada si no es app nativa o no hay actualización
  if (!isNativeApp) return null;

  // Modal para actualizaciones forzadas
  if (isForceUpdate && versionInfo) {
    return (
      <AlertDialog open={true}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-full bg-destructive/10">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <AlertDialogTitle className="text-xl">
                  Actualización Requerida
                </AlertDialogTitle>
                <p className="text-sm text-muted-foreground">
                  v{versionInfo.version}
                </p>
              </div>
            </div>
            <AlertDialogDescription className="text-left">
              <p className="mb-4">
                Esta versión de la aplicación ya no es compatible. Debes
                actualizar para continuar usando Madelmold.
              </p>

              {versionInfo.changelog && versionInfo.changelog.length > 0 && (
                <div className="space-y-2">
                  <p className="font-medium text-foreground">Novedades:</p>
                  <ul className="space-y-1">
                    {versionInfo.changelog.map((item, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm"
                      >
                        <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button onClick={downloadUpdate} className="w-full gap-2">
              <Download className="w-4 h-4" />
              Descargar Actualización
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Banner para actualizaciones opcionales
  if (showBanner && hasUpdate && versionInfo) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300 md:left-auto md:right-4 md:max-w-md">
        <Card className="border-primary/30 shadow-lg bg-gradient-to-r from-background via-background to-primary/5">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">
                    Nueva Versión Disponible
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      v{currentVersion}
                    </Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge className="text-xs bg-primary">
                      v{versionInfo.version}
                    </Badge>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -mt-1 -mr-2"
                onClick={() => {
                  setShowBanner(false);
                  dismissUpdate();
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {versionInfo.changelog && versionInfo.changelog.length > 0 && (
              <ul className="space-y-1 mb-4 text-sm text-muted-foreground">
                {versionInfo.changelog.slice(0, 3).map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-accent mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
                {versionInfo.changelog.length > 3 && (
                  <li className="text-xs text-muted-foreground pl-5">
                    +{versionInfo.changelog.length - 3} más...
                  </li>
                )}
              </ul>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setShowBanner(false);
                  dismissUpdate();
                }}
              >
                Más tarde
              </Button>
              <Button size="sm" className="flex-1 gap-2" onClick={downloadUpdate}>
                <Download className="w-4 h-4" />
                Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

// Componente para mostrar info de versión en Settings
export const VersionInfo = () => {
  const {
    currentVersion,
    currentVersionCode,
    isChecking,
    hasUpdate,
    versionInfo,
    checkForUpdates,
    downloadUpdate,
    isNativeApp,
  } = useAppUpdate();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Smartphone className="w-4 h-4" />
          Información de la Aplicación
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Versión actual</span>
          <div className="flex items-center gap-2">
            <Badge variant="outline">v{currentVersion}</Badge>
            <span className="text-xs text-muted-foreground">
              (build {currentVersionCode})
            </span>
          </div>
        </div>

        {isNativeApp && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Estado</span>
              {isChecking ? (
                <div className="flex items-center gap-2 text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Verificando...
                </div>
              ) : hasUpdate ? (
                <Badge className="bg-primary">
                  v{versionInfo?.version} disponible
                </Badge>
              ) : (
                <span className="text-sm text-accent flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Actualizada
                </span>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={checkForUpdates}
                disabled={isChecking}
              >
                {isChecking ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Buscar Actualizaciones
              </Button>
              {hasUpdate && (
                <Button size="sm" className="flex-1 gap-2" onClick={downloadUpdate}>
                  <Download className="w-4 h-4" />
                  Descargar
                </Button>
              )}
            </div>
          </>
        )}

        {!isNativeApp && (
          <p className="text-xs text-muted-foreground">
            Las actualizaciones automáticas solo están disponibles en la aplicación móvil.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
